import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Search, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { isAlertActive, formatAnnouncementDate } from '@/lib/announcements';
import { richTextToPlain } from '@/lib/alertHeroStyle';
import {
  getAccountAlerts,
  dismissAccountAlert,
  undismissAccountAlert,
  type AccountAlert,
} from '@/services/accountAlerts.service';
import { toast } from '@/store/toastStore';
import { AlertHeroCardModal } from '@/components/dashboard/AlertHeroCardModal';
import { AlertHeroTile } from '@/components/dashboard/AlertHeroTile';

type StatusKind = 'current' | 'closed' | 'past';

// Current announcements carry no label; only the exceptions are marked, in a
// pill the card draws in its own colors so it blends with any background.
const STATUS_LABEL: Partial<Record<StatusKind, string>> = {
  closed: 'Closed',
  past: 'Past',
};

/** Lower-cased visible text of rich-text HTML, with entities (&amp;) decoded. */
function searchableText(html?: string): string {
  const plain = richTextToPlain(html);
  if (!plain.includes('&') || typeof DOMParser === 'undefined') return plain.toLowerCase();
  // Tags are already stripped, so this only decodes entities.
  const decoded = new DOMParser().parseFromString(plain, 'text/html').body.textContent;
  return (decoded ?? plain).toLowerCase();
}

export function AnnouncementsArchivePage() {
  const { user } = useAuth();
  const userTz = useUserTimezone();
  const queryClient = useQueryClient();
  // The announcement open in the full-card modal. An id rather than the record,
  // so a close/restore refetch shows up in the open modal too.
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['account-alerts'],
    queryFn: getAccountAlerts,
    staleTime: 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['account-alerts'] });

  const dismissMutation = useMutation({
    mutationFn: (id: number) => dismissAccountAlert(id),
    onSuccess: invalidate,
    onError: () => toast.error('Could not close the announcement.'),
  });
  const undismissMutation = useMutation({
    mutationFn: (id: number) => undismissAccountAlert(id),
    onSuccess: invalidate,
    onError: () => toast.error('Could not reopen the announcement.'),
  });

  const items = useMemo(() => {
    if (!user) return [];
    return alerts
      .filter((a) => a.active && user.userLevel >= a.userLevel)
      .slice()
      .sort((a, b) =>
        (b.createDate || b.startDate || '').localeCompare(a.createDate || a.startDate || ''),
      );
  }, [alerts, user]);

  // Built once per data load rather than per keystroke. The title, the copy, the
  // More Details copy and the date, so a search finds what the modal shows too.
  const searchIndex = useMemo(
    () =>
      items.map((alert) => ({
        alert,
        text: [
          searchableText(alert.alertTitle),
          searchableText(alert.alertContent),
          searchableText(alert.moreDetails),
          (formatAnnouncementDate(alert) ?? '').toLowerCase(),
        ].join('\n'),
      })),
    [items],
  );

  const query = search.trim().toLowerCase();
  const visible = useMemo(
    () => (query ? searchIndex.filter((e) => e.text.includes(query)).map((e) => e.alert) : items),
    [query, searchIndex, items],
  );

  // Looked up in the full list, not the filtered one, so typing in the search
  // never pulls an open modal out from under the reader.
  const viewing =viewingId === null ? null : (items.find((a) => a.accountAlertID === viewingId) ?? null);

  const statusOf = (a: AccountAlert): StatusKind => {
    if (a.dismissedAt) return 'closed';
    if (!isAlertActive(a, userTz)) return 'past';
    return 'current';
  };

  return (
    <div className="p-6 max-md:p-4">
      <PageHeader
        title="Announcements"
        subtitle="Updates and announcements from Prospera. Closed items stay here for reference."
      />

      {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Bell className="h-8 w-8 text-slate-400" />}
            title="No announcements"
            description="There are no announcements to show right now."
          />
        ) : (
          <>
            {/* Pinned to the top of the scrollport: <main> from md, the document
                below it. The negative margins run the bar's background out over
                the page gutters so tiles scrolling under it are masked edge to
                edge, and z-20 lifts it over the tiles, which are positioned too. */}
            <div className="sticky top-0 z-20 -mx-6 mb-2 flex items-center gap-3 bg-background px-6 py-3 max-md:-mx-4 max-md:px-4">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearch('');
                }}
                placeholder="Search announcements…"
                aria-label="Search announcements"
                wrapperClassName="max-w-md"
                leftAddon={<Search className="h-4 w-4" />}
                rightAddon={
                  search ? (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="rounded p-0.5 text-slate-400 hover:text-slate-600"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : undefined
                }
              />
              <span className="shrink-0 text-xs text-slate-500 tabular-nums" aria-live="polite">
                {query ? `${visible.length} of ${items.length}` : `${items.length} total`}
              </span>
            </div>

            {visible.length === 0 ? (
              <EmptyState
                icon={<Search className="h-8 w-8 text-slate-400" />}
                title="No matches"
                description={`No announcements match “${search.trim()}”.`}
              />
            ) : (
              /* Tiles on the dashboard banner's backgrounds: date, title, the
                 status pill and the close/reopen control, and a View details
                 button that opens the full card in a modal.

                 Up to five per row, filling the row. Each column is the larger
                 of 16rem and a fifth of the row (less the four 1rem gaps), so a
                 sixth never fits; once five 16rem tiles no longer fit, a row
                 holds fewer and the rest wrap. auto-fit, not auto-fill, so with
                 fewer tiles than columns the empty columns collapse and the
                 tiles share the row - which also covers a search that matches
                 only a few. 16rem is the narrowest a tile's top line holds the
                 date, a status pill and the 44px touch close control.
                 min(16rem, 100%) keeps one column inside a phone narrower than
                 that. */
              <div className="grid grid-cols-[repeat(auto-fit,minmax(max(min(16rem,100%),calc((100%_-_4rem)/5)),1fr))] gap-4">
                {visible.map((alert) => {
                  const status = statusOf(alert);
                  const busy =
                    (dismissMutation.isPending && dismissMutation.variables === alert.accountAlertID) ||
                    (undismissMutation.isPending && undismissMutation.variables === alert.accountAlertID);
                  return (
                    <AlertHeroTile
                      key={alert.accountAlertID}
                      alert={alert}
                      dateLabel={formatAnnouncementDate(alert)}
                      onView={() => setViewingId(alert.accountAlertID)}
                      statusLabel={STATUS_LABEL[status]}
                      dismissed={status === 'closed'}
                      dismissDisabled={busy}
                      onDismiss={() =>
                        status === 'closed'
                          ? undismissMutation.mutate(alert.accountAlertID)
                          : dismissMutation.mutate(alert.accountAlertID)
                      }
                    />
                  );
                })}
              </div>
            )}
          </>
        )}

      <AlertHeroCardModal
        alert={viewing}
        dateLabel={viewing ? formatAnnouncementDate(viewing) : null}
        statusLabel={viewing ? STATUS_LABEL[statusOf(viewing)] : undefined}
        onClose={() => setViewingId(null)}
      />
    </div>
  );
}
